import React from "react";
import { Col, Container, Row } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import ScheduledChangesIcon from "../assets/images/timetable_rgb_red.svg";
import "../assets/styles/scheduledChanges.scss";
import ScheduledChangeDetailButtons from "../components/ScheduledChanges/Details/ScheduledChangeDetailButtons";
import ScheduledChangeDetailData from "../components/ScheduledChanges/Details/ScheduledChangeDetailData";
import ScheduledChangeDetailRecords from "../components/ScheduledChanges/Details/ScheduledChangeDetailRecords";
import ScheduledChangeDetailUpdates from "../components/ScheduledChanges/Details/ScheduledChangeDetailUpdates";
import ScheduledChangeDetailFilters from "../components/ScheduledChanges/Details/ScheduledChangeDetailFilters";
import {
  deleteScheduledChange,
  getScheduledChangeDetails,
  approveScheduledChange,
  rejectScheduledChange,
} from "../services/scheduledChangesApi";

const ScheduledChangeDetails = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [isDeleting, setIsDeleting] = React.useState(false);
  const [isApproving, setIsApproving] = React.useState(false);
  const [isRejecting, setIsRejecting] = React.useState(false);
  const [details, setDetails] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(false);
  const [loadError, setLoadError] = React.useState(null);

  React.useEffect(() => {
    if (!id) return;

    let cancelled = false;
    const controller = new AbortController();

    (async () => {
      setIsLoading(true);
      setLoadError(null);
      try {
        const response = await getScheduledChangeDetails(id, controller.signal);
        if (cancelled) return;

        const payload = response?.data;
        const record = payload?.data ?? payload;
        setDetails(record && typeof record === "object" ? record : null);
      } catch {
        if (!cancelled) {
          setDetails(null);
          setLoadError("Nie udało się pobrać szczegółów zmiany.");
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [id]);

  const handleEdit = React.useCallback(() => {
    if (!id) return;
    navigate(`/scheduled-changes/update/${id}`);
  }, [id, navigate]);

  const handleDelete = React.useCallback(async () => {
    if (!id || isDeleting) return;

    const confirmed = window.confirm("Czy na pewno chcesz usunąć ten rekord?");
    if (!confirmed) return;

    setIsDeleting(true);
    try {
      await deleteScheduledChange(id);
      alert("Rekord został usunięty.");
      navigate("/scheduled-changes");
    } catch {
      alert("Nie udało się usunąć rekordu.");
    } finally {
      setIsDeleting(false);
    }
  }, [id, isDeleting, navigate]);

  const handleApprove = React.useCallback(async () => {
    if (!id || isApproving) return;

    setIsApproving(true);
    try {
      await approveScheduledChange(id);
      alert("Zmiana została zaakceptowana.");
      navigate("/scheduled-changes");
    } catch {
      alert("Nie udało się zaakceptować zmiany.");
    } finally {
      setIsApproving(false);
    }
  }, [id, isApproving, navigate]);

  const handleReject = React.useCallback(async () => {
    if (!id || isRejecting) return;

    setIsRejecting(true);
    try {
      await rejectScheduledChange(id);
      alert("Zmiana została odrzucona.");
      navigate("/scheduled-changes");
    } catch {
      alert("Nie udało się odrzucić zmiany.");
    } finally {
      setIsRejecting(false);
    }
  }, [id, isRejecting, navigate]);

  const isEditDisabled = React.useMemo(() => {
    const rawStatus = details?.status;
    if (rawStatus === null || rawStatus === undefined) return false;

    const normalizedStatus = String(rawStatus).trim().toLowerCase();
    const blockedStatuses = new Set([
      "wykonane",
      "wycofana",
      "wycofane",
      "w trakcie wykonywania",
      "executed",
      "withdrawn",
      "queued",
    ]);

    return blockedStatuses.has(normalizedStatus);
  }, [details?.status]);

  return (
    <div className="users-container">
      <Container fluid>
        <Row className="align-items-center mb-4">
          <Col xs="auto">
            <img
              src={ScheduledChangesIcon}
              alt="Scheduled Changes Icon"
              style={{ width: "30px", height: "30px" }}
            />
          </Col>
          <Col>
            <h3 style={{ fontSize: "1.5rem", fontWeight: "bold" }}>
              Szczegóły zaplanowanej zmiany
            </h3>
          </Col>
        </Row>
        <Row className="mb-3">
          <Col>
            <ScheduledChangeDetailButtons
              onEdit={handleEdit}
              onDelete={handleDelete}
              isDeleting={isDeleting}
              isEditDisabled={isEditDisabled}
              onApprove={handleApprove}
              onReject={handleReject}
              isApproving={isApproving}
              isRejecting={isRejecting}
            />
          </Col>
        </Row>
        <Row className="g-3 g-xl-4">
          <Col xl={8} className="sc-details-main-col">
            <div className="d-flex flex-column gap-3">
              {isLoading && (
                <div className="text-muted mb-2">Ładowanie szczegółów…</div>
              )}
              {loadError && <div className="text-danger mb-2">{loadError}</div>}
              <ScheduledChangeDetailData details={details || {}} />
              <ScheduledChangeDetailRecords
                changeDetailsJson={details?.changeDetailsJson}
              />
            </div>
          </Col>
          <Col xl={4} className="sc-details-side-col">
            <div className="d-flex flex-column gap-3">
              <ScheduledChangeDetailFilters
                changeDetailsJson={details?.changeDetailsJson}
              />
              <ScheduledChangeDetailUpdates
                changeDetailsJson={details?.changeDetailsJson}
              />
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default ScheduledChangeDetails;
